import { CommonModule } from "@angular/common";
import { Component, forwardRef, Input } from "@angular/core";
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from "@angular/forms";
import { FormMaxLengths } from "../../../core/validators/form-max-lengths";
import {
    SIGNATURE_INVALID_CHARS_MESSAGE,
    signatureNameValidator,
} from "../../../core/validators/signature-name.validator";
import { SignedByLengths } from "../../../core/validators/form-max-lengths";

import {FormValueSyncDirective} from "../../../core/directives/form-value-sync.directive";
@Component({
    selector: "app-signature-input",
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormValueSyncDirective],
    template: `
        <input
            type="text"
            [id]="inputId"
            [attr.name]="inputId"
            autocomplete="off"
            class="form-control"
            [class.is-invalid]="showErrors"
            [placeholder]="placeholder"
            [maxlength]="maxLength"
            [formControl]="innerControl"
            (blur)="onTouched()" />
        <span *ngIf="showErrors" class="text-danger d-block mt-1">
            {{ errorMessage }}
        </span>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignatureInputComponent),
            multi: true,
        },
    ],
})
export class SignatureInputComponent implements ControlValueAccessor {
    @Input() inputId: string = "signedBy";
    @Input() placeholder: string = "";
    @Input() showFormErrors: boolean = false;
    @Input() requiredMessage: string = "Electronic signature is required";

    readonly maxLength = FormMaxLengths.Name;
    readonly minLength = SignedByLengths.Min;

    innerControl = new FormControl<string | null>(null);
    onTouched: () => void = () => {};

    private _onChange: (value: string | null) => void = () => {};
    private _externalControl: AbstractControl | null = null;

    constructor() {
        this.innerControl.valueChanges.subscribe((value) => {
            this._onChange(value ?? null);
        });
    }

    writeValue(value: string | null): void {
        this.innerControl.setValue(value ?? null, { emitEvent: false });
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.innerControl.disable({ emitEvent: false });
        } else {
            this.innerControl.enable({ emitEvent: false });
        }
    }

    @Input()
    set externalControl(control: AbstractControl | null) {
        this._externalControl = control;
    }

    get showErrors(): boolean {
        const ctrl = this._externalControl ?? this.innerControl;
        if (!ctrl || !ctrl.errors) return false;
        return this.showFormErrors || ctrl.dirty || ctrl.touched;
    }

    get errorMessage(): string {
        const ctrl = this._externalControl ?? this.innerControl;
        if (!ctrl?.errors) return "";

        if (ctrl.errors["required"]) return this.requiredMessage;
        if (ctrl.errors["minlength"]) {
            return `Electronic signature must be at least ${SignedByLengths.Min} characters`;
        }
        if (ctrl.errors["maxlength"]) {
            return `Electronic signature must be at most ${SignedByLengths.Max} characters`;
        }
        if (ctrl.errors["signatureCharacters"]) return SIGNATURE_INVALID_CHARS_MESSAGE;
        return "";
    }

    static readonly validator = signatureNameValidator;
}
